import type { IDataTableColumn, IDataTableExportRequest } from '../DataTable.types';
import { exportDataTableToExcel } from '../dataTableExcelExport';
import { columnExcelCell } from '../dataTableFormat';
import type { ServerLoader } from '../dataTableServerSource';

/**
 * Выгрузка в .xlsx всей выборки DXLO-списка (`export` источника). Клиентский экспорт таблицы взял бы
 * только подгруженные прокруткой строки, а экспорт DX запрашивал выборку целиком по тем же фильтру и
 * сортировке — так и здесь. `take: 0` — «без страницы»: эндпоинт отдаёт всё со своим потолком, как на
 * запрос выгрузки DX. Колонки и их порядок — видимые в таблице.
 */
export function createDxloExcelExport<T>(
    load: ServerLoader<T>,
    fileName: string,
    columns: readonly IDataTableColumn<T>[],
): (request: IDataTableExportRequest, signal: AbortSignal) => Promise<void> {
    return async ({ loadOptions, columns: shown }, signal) => {
        const { rows } = await load(
            { sort: loadOptions?.sort, filter: loadOptions?.filter, skip: 0, take: 0, requireTotalCount: true },
            signal,
        );
        const byField = new Map(columns.map((column) => [column.field, column]));
        const exported = [...shown]
            .sort((a, b) => a.visibleIndex - b.visibleIndex)
            .flatMap((column) => byField.get(column.field) ?? []);
        await exportDataTableToExcel({
            fileName: `${fileName}.xlsx`,
            headers: exported.map((column) => column.header),
            rows: rows.map((row) =>
                exported.map((column) =>
                    columnExcelCell(
                        column,
                        row,
                        column.accessor ? column.accessor(row) : (row as Record<string, unknown>)[column.field],
                    ),
                ),
            ),
        });
    };
}
